/**
 * 미스터리(?) 블록 일일 보상 카운터 — 순수 로직(Phaser 비의존, 단위 테스트 대상). ModuleFusionController 동형.
 *
 * 미스터리 블록을 깰 때 주는 다이아·유물의 "하루 지급 횟수"를 카운트하고 한도 도달 시 추가 지급을 막는다
 * (요건: 다이아·유물은 하루에 나오는 양 제한). 한도는 global.tsv(MYSTERY_BRICK.diamondDailyLimit/
 * relicDailyLimit) 주입 — 코드 리터럴 0. 자정 리셋은 @yjsgames/daily-reset의 DailyResetService
 * ("mystery-block" 키)가 구동하고, 리셋 시 boot의 onReset 콜백이 applyReset()을 호출한다(던전/융합 동형).
 * 실제 재화·유물 지급은 호출자(BattlefieldRunner.grantMysteryReward) 책임 — 본 컨트롤러는 카운터만 관리.
 */

/** 미스터리 일일 카운터 영속 상태 — 자정 리셋 시각은 dailyResets["mystery-block"]가 SSoT라 여기엔 없다. */
export interface MysteryBlockState {
  diamondGrantedToday: number;
  relicGrantedToday: number;
}

/** 카운터 정규화 — 음수·소수·NaN·undefined를 0 이상 정수로 안전 변환(저장 손상 방어). */
function clampCount(x: number | undefined): number {
  const n = Math.floor(x ?? 0);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

export class MysteryBlockController {
  private diamondCount: number;
  private relicCount: number;
  private readonly diamondLimit: number;
  private readonly relicLimit: number;

  constructor(
    initial: Partial<MysteryBlockState> | undefined,
    diamondLimit: number,
    relicLimit: number,
  ) {
    this.diamondCount = clampCount(initial?.diamondGrantedToday);
    this.relicCount = clampCount(initial?.relicGrantedToday);
    this.diamondLimit = clampCount(diamondLimit);
    this.relicLimit = clampCount(relicLimit);
  }

  /** 오늘 지급한 다이아 횟수 (자정 리셋 대상). */
  get diamondGrantedToday(): number {
    return this.diamondCount;
  }

  /** 오늘 지급한 유물 횟수 (자정 리셋 대상). */
  get relicGrantedToday(): number {
    return this.relicCount;
  }

  /** 오늘 남은 다이아 지급 가능 횟수 (UI·AutoPlay dump). */
  get diamondRemaining(): number {
    return Math.max(0, this.diamondLimit - this.diamondCount);
  }

  /** 오늘 남은 유물 지급 가능 횟수 (UI·AutoPlay dump). */
  get relicRemaining(): number {
    return Math.max(0, this.relicLimit - this.relicCount);
  }

  /** 오늘 다이아를 더 줄 수 있는지 (한도 미도달). */
  canGrantDiamond(): boolean {
    return this.diamondCount < this.diamondLimit;
  }

  /** 오늘 유물을 더 줄 수 있는지 (한도 미도달). */
  canGrantRelic(): boolean {
    return this.relicCount < this.relicLimit;
  }

  /** 다이아 1회 지급 기록 — 호출자가 실제 지급 후 호출. */
  markDiamondGranted(): void {
    this.diamondCount += 1;
  }

  /** 유물 1회 지급 기록 — 호출자가 실제 지급 후 호출. */
  markRelicGranted(): void {
    this.relicCount += 1;
  }

  /** 자정 리셋 — onReset 콜백에서 호출. 두 카운터 0 복귀(다이아·유물 동시 리셋). */
  applyReset(): void {
    this.diamondCount = 0;
    this.relicCount = 0;
  }

  /** 영속 상태 스냅샷 — SaveData 직렬화용 (생성자 initial로 round-trip 복원). */
  toState(): MysteryBlockState {
    return { diamondGrantedToday: this.diamondCount, relicGrantedToday: this.relicCount };
  }
}
